import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { PublicKey } from '@solana/web3.js';
import { BN } from '@coral-xyz/anchor';
import { useProgramContext } from '../contexts/ProgramContext';
import { verifyAdminAndGetApiKey } from '../utils/adminCheck';

interface EpochInfo {
  epochIndex: number;
  epochStart: number;
  epochEnd: number;
  epochEp: string;
  isFinalized: boolean;
  rewardsMerkleRoot: number[];
}

interface GlobalEpochState {
  currentEpochIndex: number;
  currentEpochStart: number;
  currentEpochEnd: number;
  stakeMint: string;
  globalBalance: string;
}

const EpochManagement: React.FC = () => {
  const wallet = useWallet();
  const { publicKey } = wallet;
  const { program } = useProgramContext();
  const [loading, setLoading] = useState(false);

  const [globalState, setGlobalState] = useState<GlobalEpochState | null>(null);
  const [currentEpoch, setCurrentEpoch] = useState<EpochInfo | null>(null);

  const [epochEp, setEpochEp] = useState('1000000');
  const [epochStart, setEpochStart] = useState('');
  const [epochEnd, setEpochEnd] = useState('');

  // Admin verification state
  const [adminApiKey, setAdminApiKey] = useState<string | null>(null);
  const [isVerifyingAdmin, setIsVerifyingAdmin] = useState(false);
  const [adminVerificationError, setAdminVerificationError] = useState<string | null>(null);

  const fetchGlobalState = async () => {
    if (!program) return;

    try {
      const [globalEpochState] = PublicKey.findProgramAddressSync(
        [Buffer.from('global_epoch_state')],
        program.programId
      );

      const state = await program.account.globalEpochState.fetch(globalEpochState);

      setGlobalState({
        currentEpochIndex: state.currentEpochIndex.toNumber(),
        currentEpochStart: state.currentEpochStart.toNumber(),
        currentEpochEnd: state.currentEpochEnd.toNumber(),
        stakeMint: state.stakeMint.toString(),
        globalBalance: state.globalBalance.toString(),
      });

      if (state.currentEpochIndex.toNumber() > 0) {
        await fetchEpochInfo(state.currentEpochIndex.toNumber());
      }
    } catch (error) {
      console.error('Error fetching global state:', error);
    }
  };

  const fetchEpochInfo = async (epochIndex: number) => {
    if (!program) return;

    try {
      const [epochPda] = PublicKey.findProgramAddressSync(
        [Buffer.from('epoch'), new BN(epochIndex).toArrayLike(Buffer, 'le', 8)],
        program.programId
      );

      const epoch = await program.account.epoch.fetch(epochPda);
      const merkleRoot = Array.from(epoch.rewardsMerkleRoot);
      const isFinalized = merkleRoot.some(byte => byte !== 0);

      setCurrentEpoch({
        epochIndex: epoch.epochIndex.toNumber(),
        epochStart: epoch.epochStart.toNumber(),
        epochEnd: epoch.epochEnd.toNumber(),
        epochEp: epoch.epochEp.toString(),
        isFinalized,
        rewardsMerkleRoot: merkleRoot,
      });
    } catch (error) {
      console.error('Error fetching epoch info:', error);
      setCurrentEpoch(null);
    }
  };

  useEffect(() => {
    fetchGlobalState();
    // Poll every 60 seconds instead of 10s to reduce RPC calls
    const interval = setInterval(() => fetchGlobalState(), 60000);
    return () => clearInterval(interval);
  }, [program]);

  // Also refresh when page becomes visible (user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && program) {
        fetchGlobalState();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [program]);

  // Verify admin status when wallet connects
  useEffect(() => {
    const verifyAdmin = async () => {
      if (!wallet.publicKey || !wallet.signMessage) {
        setAdminApiKey(null);
        setAdminVerificationError(null);
        return;
      }

      setIsVerifyingAdmin(true);
      setAdminVerificationError(null);

      try {
        const result = await verifyAdminAndGetApiKey(wallet as any);
        
        if (result.success && result.isAdmin && result.adminApiKey) {
          setAdminApiKey(result.adminApiKey);
          setAdminVerificationError(null);
        } else {
          setAdminApiKey(null);
          setAdminVerificationError(result.error || 'Not authorized as admin');
        }
      } catch (error: any) {
        console.error('Admin verification failed:', error);
        setAdminApiKey(null);
        setAdminVerificationError(error.message || 'Verification failed');
      } finally {
        setIsVerifyingAdmin(false);
      }
    };

    verifyAdmin();
  }, [wallet.publicKey, wallet.signMessage]);

  const handleStartEpoch = async () => {
    if (!program || !publicKey || !epochEp || !epochStart || !epochEnd) {
      alert('Please fill in all fields');
      return;
    }

    setLoading(true);
    const nextEpochIndex = globalState ? globalState.currentEpochIndex + 1 : 1;
    
    try {
      const startTimestamp = Math.floor(new Date(epochStart).getTime() / 1000);
      const endTimestamp = Math.floor(new Date(epochEnd).getTime() / 1000);

      const tx = await program.methods
        .startEpoch(
          new BN(nextEpochIndex),
          new BN(epochEp),
          new BN(startTimestamp),
          new BN(endTimestamp)
        )
        .accounts({
          admin: publicKey,
        })
        .rpc();

      alert(`Epoch ${nextEpochIndex} started successfully!\n\nTransaction: ${tx}`);
      
      // Wait for state to update
      await new Promise(resolve => setTimeout(resolve, 2000));
      await fetchGlobalState();
      
      // Clear form
      setEpochEp('1000000');
      setEpochStart('');
      setEpochEnd('');
    } catch (error: any) {
      console.error('Error starting epoch:', error);
      
      // Check if the error is about transaction already processed
      const isAlreadyProcessed = 
        error.message?.includes('already been processed') ||
        error.message?.includes('already processed') ||
        error.message?.includes('This transaction has already been processed');
      
      if (isAlreadyProcessed) {
        // Transaction might have succeeded, verify by checking current epoch
        console.log('Transaction already processed, verifying epoch creation...');
        
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Fetch fresh state to verify
        try {
          const [globalEpochState] = PublicKey.findProgramAddressSync(
            [Buffer.from('global_epoch_state')],
            program.programId
          );
          const state = await program.account.globalEpochState.fetch(globalEpochState);
          const currentIndex = state.currentEpochIndex.toNumber();
          
          if (currentIndex >= nextEpochIndex) {
            alert(`Epoch ${nextEpochIndex} has been started successfully!`);
            
            // Refresh full state
            await fetchGlobalState();
            
            // Clear form
            setEpochEp('1000000');
            setEpochStart('');
            setEpochEnd('');
            setLoading(false);
            return;
          }
        } catch (verifyError) {
          console.error('Error verifying epoch creation:', verifyError);
        }
        
        // If we can't verify, show helpful message
        alert('Transaction may have already been processed. Refreshing data...');
        await fetchGlobalState();
        setLoading(false);
        return;
      }
      
      // Show error for other cases
      const errorMsg = error.message || 'Failed to start epoch';
      alert(`Error: ${errorMsg.length > 200 ? 'Transaction failed. Please check if the epoch was created and try again.' : errorMsg}`);
    }
    setLoading(false);
  };

  const handleCalculateRewards = async () => {
    if (!currentEpoch) {
      alert('No active epoch to calculate rewards for');
      return;
    }

    if (!adminApiKey) {
      alert('Admin verification required. Please connect your wallet and ensure you are authorized as an admin.');
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    if (now < currentEpoch.epochEnd) {
      alert('Cannot calculate rewards until epoch has ended');
      return;
    }

    setLoading(true);
    try {
      const API_URL = import.meta.env.VITE_INDEXER_URL || 'https://habitat-helius-indexer.habitatsystemacc.workers.dev';

      // Try reward worker direct endpoint first (faster, synchronous)
      let response = await fetch(`${API_URL}/rewards/calculate/${currentEpoch.epochIndex}`, {
        method: 'POST',
        headers: {
          'x-admin-api-key': adminApiKey,
        },
      });

      if (!response.ok) {
        // Fallback to queue endpoint
        response = await fetch(`${API_URL}/rewards/calculate/${currentEpoch.epochIndex}`, {
          method: 'POST',
          headers: {
            'x-admin-api-key': adminApiKey,
          },
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to calculate rewards: ${response.statusText}`);
      }

      const result = await response.json();
      alert(`Epoch ${currentEpoch.epochIndex} finalized successfully!\n\nUsers: ${result.totalUsers || 'queued'}\nMerkle Root: ${result.merkleRoot || 'calculating...'}\n\nRewards are now claimable!`);
      await fetchGlobalState();
    } catch (error) {
      console.error('Error calculating rewards:', error);
      alert(`Error: ${error}`);
    }
    setLoading(false);
  };

  const getTimeRemaining = () => {
    if (!currentEpoch) return null;

    const now = Math.floor(Date.now() / 1000);
    const { epochStart, epochEnd } = currentEpoch;

    if (now < epochStart) {
      return { status: 'pending', seconds: epochStart - now };
    } else if (now >= epochStart && now < epochEnd) {
      return { status: 'active', seconds: epochEnd - now };
    } else {
      return { status: 'ended', seconds: 0 };
    }
  };

  const formatTimeRemaining = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const setDefaultTimes = () => {
    const now = new Date();
    const start = new Date(now.getTime() + 60000);
    const end = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000);

    setEpochStart(start.toISOString().slice(0, 16));
    setEpochEnd(end.toISOString().slice(0, 16));
  };

  const timeRemaining = getTimeRemaining();

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-habitat-neutral-900">Epoch Management</h2>

      {/* Admin Verification Status */}
      {publicKey && (
        <div className={`card ${adminApiKey ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-habitat-neutral-900">
                {isVerifyingAdmin ? '🔄 Verifying Admin Status...' : adminApiKey ? '✅ Admin Verified' : '⚠️ Admin Verification Required'}
              </h3>
              {adminApiKey && (
                <p className="text-sm text-green-700 mt-1">
                  You have admin privileges and can manage epochs and calculate rewards.
                </p>
              )}
              {adminVerificationError && (
                <p className="text-sm text-yellow-700 mt-1">
                  {adminVerificationError}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {globalState && (
        <div className="card">
          <h3 className="text-xl font-bold text-habitat-neutral-900 mb-4">Current Epoch State</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-habitat-neutral-500 mb-1">Current Epoch</p>
              <p className="text-2xl font-bold text-habitat-primary">#{globalState.currentEpochIndex}</p>
            </div>
            <div>
              <p className="text-sm text-habitat-neutral-500 mb-1">Global Balance</p>
              <p className="text-2xl font-bold text-habitat-secondary">{(Number(globalState.globalBalance) / 1e6).toFixed(4)} tokens</p>
            </div>
            {currentEpoch && (
              <>
                <div>
                  <p className="text-sm text-habitat-neutral-500 mb-1">Epoch EP Pool</p>
                  <p className="text-2xl font-bold text-habitat-accent">{Number(currentEpoch.epochEp).toLocaleString()} EP</p>
                </div>
                <div>
                  <p className="text-sm text-habitat-neutral-500 mb-1">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                    currentEpoch.isFinalized
                      ? 'bg-green-100 text-green-800'
                      : timeRemaining?.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800'
                      : timeRemaining?.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {currentEpoch.isFinalized
                      ? 'Finalized'
                      : timeRemaining?.status === 'pending'
                      ? 'Pending'
                      : timeRemaining?.status === 'active'
                      ? 'Active'
                      : 'Ended'}
                  </span>
                </div>
                {timeRemaining && timeRemaining.seconds > 0 && (
                  <div>
                    <p className="text-sm text-habitat-neutral-500 mb-1">
                      {timeRemaining.status === 'pending' ? 'Starts in' : 'Ends in'}
                    </p>
                    <p className="text-2xl font-bold text-habitat-neutral-900">{formatTimeRemaining(timeRemaining.seconds)}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-habitat-neutral-500 mb-1">Start Time</p>
                  <p className="text-sm font-semibold text-habitat-neutral-900">{new Date(currentEpoch.epochStart * 1000).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-habitat-neutral-500 mb-1">End Time</p>
                  <p className="text-sm font-semibold text-habitat-neutral-900">{new Date(currentEpoch.epochEnd * 1000).toLocaleString()}</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="text-xl font-bold text-habitat-neutral-900 mb-4">Start New Epoch</h3>
        {globalState && (
          <p className="text-sm text-habitat-neutral-600 mb-4">
            Next Epoch: <strong className="text-habitat-primary">#{globalState.currentEpochIndex + 1}</strong>
          </p>
        )}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-habitat-neutral-700 mb-2">Epoch EP Pool</label>
            <input
              type="number"
              value={epochEp}
              onChange={(e) => setEpochEp(e.target.value)}
              placeholder="1000000"
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-habitat-neutral-700 mb-2">Epoch Start Time</label>
            <input
              type="datetime-local"
              value={epochStart}
              onChange={(e) => setEpochStart(e.target.value)}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-habitat-neutral-700 mb-2">Epoch End Time</label>
            <input
              type="datetime-local"
              value={epochEnd}
              onChange={(e) => setEpochEnd(e.target.value)}
              className="input-field"
            />
          </div>
          <div className="flex gap-3">
            <button onClick={setDefaultTimes} className="btn-outline">
              Set Default (30 days)
            </button>
            <button
              onClick={handleStartEpoch}
              disabled={loading || !epochEp || !epochStart || !epochEnd || !adminApiKey}
              className={`btn-primary ${loading || !epochEp || !epochStart || !epochEnd || !adminApiKey ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={!adminApiKey ? 'Admin verification required' : ''}
            >
              {loading ? 'Starting...' : 'Start Epoch'}
            </button>
          </div>
        </div>
      </div>

      {currentEpoch && !currentEpoch.isFinalized && (
        <div className="card bg-habitat-neutral-50">
          <h3 className="text-xl font-bold text-habitat-neutral-900 mb-4">Calculate & Finalize Epoch</h3>
          <p className="text-sm text-habitat-neutral-600 mb-4">
            <strong>Note:</strong> This will calculate rewards, generate the merkle root, and automatically finalize epoch {currentEpoch.epochIndex} onchain.
          </p>
          <div className="space-y-3">
            <button
              onClick={handleCalculateRewards}
              disabled={loading || !adminApiKey}
              className={`btn-primary ${loading || !adminApiKey ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={!adminApiKey ? 'Admin verification required' : ''}
            >
              {loading ? 'Processing...' : `Calculate & Finalize Epoch ${currentEpoch.epochIndex}`}
            </button>
            <p className="text-xs text-habitat-neutral-500">
              💡 This will compute stake-seconds, calculate rewards for all users, generate the merkle root, and publish it onchain. Users will be able to claim rewards immediately after finalization.
            </p>
          </div>
        </div>
      )}

      <div className="card bg-habitat-neutral-50">
        <h3 className="font-semibold text-habitat-neutral-900 mb-3">Epoch Management Tips</h3>
        <ul className="space-y-2 text-sm text-habitat-neutral-600">
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Only admins can start and finalize epochs</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Epoch start must be after the previous epoch ends</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Epoch end must be after epoch start</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Wait for the epoch to end before calculating rewards</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Calculating rewards automatically finalizes the epoch onchain</span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>Once finalized, users can immediately claim their rewards</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default EpochManagement;
